'use client';

import {
  ReactNode, useEffect, useMemo, useState,
} from 'react';
import { i18n as i18nType, createInstance } from 'i18next';
import { I18nextProvider as ReactI18nextProvider } from 'react-i18next';
import initTranslations from '@app/i18n';

let globalI18n: i18nType | null = null;

/**
 * I18nextProvider
 *
 * It ensures that the `i18n` instance is reused and updated whenever the locale changes,
 * supporting both server-side and client-side rendering.
 */
export const I18nextProvider = ({ children, locale } : { children: ReactNode; locale: Locale; }) => {
  // State to manage a unique key for the provider.
  // Changing this forces React to re-render the provider.
  const [providerKey, setProviderKey] = useState(0);

  /**
   * Memoize and reuse the `i18n` instance:
   * - On initial load: Creates a new `i18n` instance if it doesn't exist.
   * - On server-side: Ensures the `i18n` instance is updated with the correct locale.
   */
  globalI18n = useMemo(() => {
    if (!globalI18n) {
      // Create a new i18n instance and initialize translations for the provided locale.
      globalI18n = createInstance();
      void initTranslations(locale, 'common', globalI18n);
    } else if (typeof window === 'undefined' && globalI18n.resolvedLanguage !== locale) {
      // Server-side logic to update the language if the locale doesn't match.
      void globalI18n.changeLanguage(locale);
    }
    return globalI18n;
  }, [locale]);

  /**
   * Client-Side Language Update Hook:
   * - It ensures the `changeLanguage` method is called only in the effect phase to prevent
   *   state updates during the render phase.
   * - Updates the `providerKey` to force a re-render of the provider when the locale changes.
   */
  useEffect(() => {
    if (globalI18n && globalI18n.resolvedLanguage !== locale) {
      void globalI18n.changeLanguage(locale).then(() => {
        // Update the provider key to force React to re-render the provider
        setProviderKey((prevKey) => prevKey + 1);
      });
    }
  }, [locale]);

  return <ReactI18nextProvider key={providerKey} i18n={globalI18n}>{children}</ReactI18nextProvider>;
};
